<template>
  <div class="home">
    <section
      v-if="slides.length"
      class="hero"
      :class="{ 'is-no-motion': noMotion }"
      role="group"
      aria-roledescription="轮播图"
      aria-label="星语流光 · 首页轮播"
    >
      <div class="hero__frame">
        <div
          ref="stage"
          class="hero__stage"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerUp"
          @mouseenter="onHover(true)"
          @mouseleave="onHover(false)"
          @focusin="onHover(true)"
          @focusout="onHover(false)"
        >
          <!-- 幻灯片层 -->
          <article
            v-for="(slide, i) in slides"
            :key="slide.key"
            class="slide"
            :class="slideState(i)"
            :aria-hidden="i === active ? 'false' : 'true'"
          >
            <div ref="media" class="slide__media">
              <img
                v-if="shownSlides[i]"
                :src="slide.src"
                :alt="slide.title"
                draggable="false"
                decoding="async"
                :loading="i === 0 ? 'eager' : 'lazy'"
                @load="onImgLoad(i)"
                @error="onImgError(i)"
              >
              <div class="slide__shade" aria-hidden="true"></div>
            </div>

            <!-- 诗意文案 -->
            <div class="slide__caption" :class="{ 'is-visible': i === active }">
              <div class="slide__eyebrow">
                <span class="slide__eyebrow-num">{{ pad(i + 1) }}</span>
                <span class="slide__eyebrow-line"></span>
                <span class="slide__eyebrow-en">{{ slide.en }}</span>
              </div>
              <h2 class="slide__title">{{ slide.title }}</h2>
              <p class="slide__sub">{{ slide.sub }}</p>
            </div>
          </article>

          <!-- 星尘粒子画布 -->
          <canvas ref="stars" class="hero__stars" aria-hidden="true"></canvas>

          <!-- 转场流光 / 暗角 / 颗粒 -->
          <div v-if="initReady" class="hero__sweep" :key="'sweep-' + sweepKey" aria-hidden="true"></div>
          <div class="hero__vignette" aria-hidden="true"></div>
          <div class="hero__grain" aria-hidden="true"></div>

          <!-- 品牌角标 -->
          <div class="hero__brand" aria-hidden="true">
            <span class="hero__brand-star">✦</span>
            <span class="hero__brand-text">PINK DRESS STAR · 粉裙星物语</span>
          </div>

          <!-- 方向控制 -->
          <button
            v-if="slides.length > 1"
            class="hero__arrow hero__arrow--prev"
            type="button"
            aria-label="上一张"
            @click="go(-1)"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button
            v-if="slides.length > 1"
            class="hero__arrow hero__arrow--next"
            type="button"
            aria-label="下一张"
            @click="go(1)"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
          </button>

          <!-- 序号与指示点 -->
          <div class="hero__meta">
            <div class="hero__fraction" aria-hidden="true">
              <span class="hero__fraction-cur">{{ pad(active + 1) }}</span>
              <span class="hero__fraction-slash">/</span>
              <span class="hero__fraction-total">{{ pad(slides.length) }}</span>
            </div>
            <div v-if="slides.length > 1" class="hero__dots" role="tablist" aria-label="选择幻灯">
              <button
                v-for="(s, i) in slides"
                :key="'dot-' + s.key"
                type="button"
                class="hero__dot"
                :class="{ 'is-active': i === active }"
                role="tab"
                :aria-label="'切换到第 ' + (i + 1) + ' 张'"
                :aria-selected="i === active ? 'true' : 'false'"
                @click="goTo(i)"
              >
                <span class="hero__dot-core"></span>
              </button>
            </div>
          </div>

          <!-- 自动播放进度条 -->
          <div class="hero__bar" :style="{ '--bar-dur': interval + 'ms' }" aria-hidden="true">
            <i :key="'bar-' + barKey" class="hero__bar-fill" :class="{ 'is-paused': pausedBar }"></i>
          </div>
        </div>

        <!-- 加载态 -->
        <transition name="hero-fade">
          <div v-if="!initReady" class="hero__loading" aria-hidden="true">
            <span class="hero__loading-star">✦</span>
            <span class="hero__loading-text">正在点亮星光…</span>
          </div>
        </transition>
      </div>

      <!-- 使用提示 -->
      <div class="hero__hint" aria-hidden="true">
        <span>✦ 共 {{ slides.length }} 幕星光</span>
        <i class="hero__hint-line"></i>
        <span>悬停暂停</span>
        <i class="hero__hint-line"></i>
        <span>方向键 / 拖拽切换</span>
      </div>
    </section>

    <div v-else class="home__empty">轮播目录为空，请将图片放入 components/Home/Carousel</div>
  </div>
</template>

<script>
/* 每张图片的文案与运镜配置：新增图片后在此追加即可（未配置的图片会自动出现在末尾） */
const SLIDE_META = {
  'login-2.png': { title: '月落星河', en: 'MOONFALL', sub: '山野村落枕着月光入眠，星河在湖心静静倒映。', ken: 'zoom-in' },
  '华为城堡.png': { title: '霓虹城堡', en: 'NEON CASTLE', sub: '紫夜的钟楼亮起灯火，童话在街角悄然上演。', ken: 'pan-right' },
  '光谷华为.png': { title: '星辉塔楼', en: 'STAR SPIRE', sub: '粉紫的灯光爬上尖顶，像把银河挂上了屋檐。', ken: 'pan-left' },
  'login-3.png': { title: '星湖古堡', en: 'LAKE LEGEND', sub: '古堡与星光在水中相遇，连风也放轻了脚步。', ken: 'zoom-in' },
  '海边.png': { title: '潮汐之约', en: 'TIDES', sub: '海浪推着白云上岸，沙滩收藏着落日与约定。', ken: 'pan-right' },
  '大自然.png': { title: '苔上新芽', en: 'MOSS & SPROUT', sub: '森林的呼吸落进苔藓，嫩芽正在阳光里苏醒。', ken: 'zoom-out' },
  '1234.jpg': { title: '熔岩花树', en: 'EMBER BLOSSOM', sub: '崖壁流火，孤树开满春色，黄昏在此停驻。', ken: 'zoom-in' },
  'login-4.png': { title: '流光隧道', en: 'LIGHT TRAIL', sub: '光点汇成星轨，带我们驶向更远的梦境。', ken: 'pan-left' },
  'download.jpg': { title: '数据星海', en: 'DATA GALAXY', sub: '指尖点亮的每一束光，都是未来的坐标。', ken: 'zoom-in' },
  'b.jpg': { title: '云上都市', en: 'SKY CITY', sub: '城市在天际线上生长，云层载着飞行的梦。', ken: 'pan-left' },
  'd.jpg': { title: '纸飞机之城', en: 'PAPER PLANE', sub: '折一架纸飞机，载着蓝图飞向更高的天空。', ken: 'zoom-out' }
}

const COLORS = ['255,255,255', '255,220,240', '201,181,255', '255,233,196', '255,180,214']

function buildSlides () {
  const files = require.context('./Carousel', false, /\.(png|jpe?g)$/i)
  const byName = {}
  files.keys().forEach((k) => {
    byName[k.replace(/^\.\//, '')] = files(k)
  })
  const names = Object.keys(byName)
  const configured = Object.keys(SLIDE_META).filter((n) => byName[n])
  const rest = names.filter((n) => !SLIDE_META[n])
  return configured.concat(rest).map((name, idx) => {
    const meta = SLIDE_META[name] || {}
    const base = name.replace(/\.(png|jpe?g)$/i, '')
    return {
      key: name + '-' + idx,
      src: byName[name],
      file: name,
      title: meta.title || base,
      en: meta.en || 'SCENERY',
      sub: meta.sub || '',
      ken: meta.ken || 'zoom-in'
    }
  })
}

export default {
  name: 'ArtWindow',
  data () {
    return {
      slides: [],
      active: 0,
      interval: 6500,
      kenDuration: 9000,
      remaining: 6500,
      timer: null,
      startedAt: 0,
      isHovering: false,
      pageHidden: false,
      shownSlides: {},
      loadedMap: {},
      pending: null,
      initReady: false,
      barKey: 0,
      sweepKey: 0,
      noMotion: false,
      pointer: null,
      raf: 0,
      kenRaf: 0,
      stars: [],
      motes: [],
      bursts: [],
      ctx: null,
      stageW: 0,
      stageH: 0,
      dpr: 1
    }
  },
  computed: {
    pausedBar () {
      return this.isHovering || this.pageHidden || this.slides.length < 2
    }
  },
  created () {
    this.slides = buildSlides()
  },
  mounted () {
    const mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null
    this.noMotion = !!(mq && mq.matches)
    const n = this.slides.length
    if (n > 0) {
      this.$set(this.shownSlides, 0, true)
      if (n > 1) this.$set(this.shownSlides, 1, true)
      this.remaining = this.interval
      this.$nextTick(() => {
        this.initStars()
        this.startKen()
        if (n > 1) this.startTimer()
        this.attachListeners()
      })
    }
  },
  beforeDestroy () {
    this.detachListeners()
    this.clearTimer()
    if (this._rsT) { clearTimeout(this._rsT); this._rsT = null }
    if (this.raf) cancelAnimationFrame(this.raf)
    if (this.kenRaf) cancelAnimationFrame(this.kenRaf)
    this.ctx = null
  },
  methods: {
    /* ---------- 幻灯片状态 ---------- */
    slideState (i) {
      const n = this.slides.length
      if (n < 2) return { 'is-active': i === this.active }
      const next = (this.active + 1) % n
      return {
        'is-active': i === this.active,
        'is-next': i === next
      }
    },
    pad (n) {
      return (n < 10 ? '0' : '') + n
    },

    /* ---------- 导航 ---------- */
    go (dir) {
      const n = this.slides.length
      if (n < 2) return
      this.setActive((this.active + dir + n) % n)
    },
    goTo (i) {
      if (i < 0 || i >= this.slides.length || i === this.active) return
      this.setActive(i)
    },
    next () {
      this.go(1)
    },
    setActive (i) {
      this.pending = i
      this.$set(this.shownSlides, i, true)
      const slide = this.slides[i]
      if (this.loadedMap[slide.src]) this.commitActive(i)
    },
    commitActive (i) {
      this.active = i
      const n = this.slides.length
      this.$set(this.shownSlides, i, true)
      if (n > 1) {
        this.$set(this.shownSlides, (i + 1) % n, true)
        this.$set(this.shownSlides, (i - 1 + n) % n, true)
      }
      this.pending = null
      this.barKey++
      this.sweepKey++
      this.startKen()
      this.emitBurst()
      if (n > 1 && !this.isHovering && !this.pageHidden) this.startTimer()
    },
    onImgLoad (i) {
      const slide = this.slides[i]
      if (slide) this.loadedMap[slide.src] = true
      if (i === 0 && !this.initReady) {
        this.$nextTick(() => { this.initReady = true })
      }
      if (this.pending === i) this.commitActive(i)
    },
    onImgError (i) {
      const slide = this.slides[i]
      if (slide) this.loadedMap[slide.src] = true
      if (i === 0 && !this.initReady) this.initReady = true
      if (this.pending === i) this.commitActive(i)
    },

    /* ---------- 自动播放 ---------- */
    startTimer () {
      this.clearTimer()
      if (this.isHovering || this.pageHidden || this.slides.length < 2) return
      this.remaining = this.interval
      this.startedAt = Date.now()
      this.timer = setTimeout(() => this.next(), this.remaining)
    },
    clearTimer () {
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
    },
    pauseTimer () {
      if (this.timer === null) return
      const elapsed = Date.now() - this.startedAt
      this.remaining = Math.max(150, this.remaining - elapsed)
      this.clearTimer()
    },
    resumeTimer () {
      if (this.isHovering || this.pageHidden || this.slides.length < 2) return
      this.startedAt = Date.now()
      this.timer = setTimeout(() => this.next(), this.remaining)
    },
    onHover (v) {
      if (this.isHovering === v) return
      this.isHovering = v
      if (v) this.pauseTimer()
      else this.resumeTimer()
    },

    /* ---------- Ken Burns 运镜 ---------- */
    startKen () {
      if (this.noMotion || !this.slides.length) return
      if (this.kenRaf) cancelAnimationFrame(this.kenRaf)
      const slide = this.slides[this.active]
      const dir = slide ? slide.ken : 'zoom-in'
      const dur = this.kenDuration
      const start = Date.now()
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)
      const tick = () => {
        const el = this.$refs.media && this.$refs.media[this.active]
        if (!el) {
          this.kenRaf = requestAnimationFrame(tick)
          return
        }
        const t = Math.min(1, (Date.now() - start) / dur)
        const e = ease(t)
        let scale = 1.06
        let tx = 0
        let ty = 0
        switch (dir) {
          case 'zoom-out': scale = 1.2 - 0.15 * e; break
          case 'pan-left': scale = 1.13; tx = -9 * e; break
          case 'pan-right': scale = 1.13; tx = 9 * e; break
          case 'pan-up': scale = 1.13; ty = -6 * e; break
          case 'pan-down': scale = 1.13; ty = 6 * e; break
          default: scale = 1.05 + 0.15 * e
        }
        el.style.transform = 'scale(' + scale.toFixed(4) + ') translate(' + tx.toFixed(2) + '%,' + ty.toFixed(2) + '%)'
        if (t < 1) this.kenRaf = requestAnimationFrame(tick)
        else this.kenRaf = 0
      }
      this.kenRaf = requestAnimationFrame(tick)
    },

    /* ---------- 星尘画布 ---------- */
    initStars () {
      if (this.noMotion) return
      const canvas = this.$refs.stars
      if (!canvas) return
      this.ctx = canvas.getContext('2d')
      this.resizeStars()
      const area = this.stageW * this.stageH
      const starCount = Math.max(26, Math.min(110, Math.round(area / 16000)))
      const moteCount = Math.max(6, Math.min(16, Math.round(area / 90000)))
      this.stars = Array.from({ length: starCount }, () => ({
        x: Math.random(),
        y: Math.random(),
        r: 0.4 + Math.random() * 1.3,
        base: 0.18 + Math.random() * 0.5,
        phase: Math.random() * Math.PI * 2,
        speed: 0.4 + Math.random() * 1.4,
        color: COLORS[(Math.random() * COLORS.length) | 0]
      }))
      this.motes = Array.from({ length: moteCount }, () => ({
        x: Math.random(),
        y: Math.random(),
        r: 1.2 + Math.random() * 2.4,
        phase: Math.random() * Math.PI * 2,
        speed: 0.25 + Math.random() * 0.6,
        sway: 0.4 + Math.random() * 0.6,
        vy: 0.00016 + Math.random() * 0.0003,
        color: COLORS[(Math.random() * COLORS.length) | 0]
      }))
      this.bursts = []
      this.raf = requestAnimationFrame(this.tick)
    },
    tick (now) {
      if (this.pageHidden) {
        this.raf = 0
        return
      }
      const ctx = this.ctx
      if (!ctx) return
      const W = this.stageW
      const H = this.stageH
      ctx.clearRect(0, 0, W, H)
      const t = now * 0.001
      for (let i = 0; i < this.stars.length; i++) {
        const s = this.stars[i]
        const a = Math.max(0, Math.min(1, s.base + Math.sin(t * s.speed + s.phase) * 0.32))
        ctx.globalAlpha = a
        ctx.fillStyle = 'rgba(' + s.color + ',1)'
        ctx.beginPath()
        ctx.arc(s.x * W, s.y * H, s.r, 0, Math.PI * 2)
        ctx.fill()
      }
      for (let i = 0; i < this.motes.length; i++) {
        const m = this.motes[i]
        m.y -= m.vy
        if (m.y < -0.05) {
          m.y = 1.05
          m.x = Math.random()
        }
        const x = (m.x + Math.sin(t * m.speed + m.phase) * m.sway * 0.02) * W
        const y = m.y * H
        ctx.globalAlpha = 0.14 + 0.1 * Math.sin(t * m.speed * 1.6 + m.phase)
        ctx.fillStyle = 'rgba(' + m.color + ',1)'
        ctx.beginPath()
        ctx.arc(x, y, m.r * 2.6, 0, Math.PI * 2)
        ctx.fill()
        ctx.globalAlpha = Math.min(0.7, 0.24 + 0.4 * Math.sin(t * m.speed * 1.6 + m.phase))
        ctx.beginPath()
        ctx.arc(x, y, m.r * 1.1, 0, Math.PI * 2)
        ctx.fill()
      }
      for (let i = this.bursts.length - 1; i >= 0; i--) {
        const p = this.bursts[i]
        p.x += p.vx
        p.y += p.vy
        p.vx *= 0.985
        p.vy *= 0.985
        p.life -= 0.018
        if (p.life <= 0) {
          this.bursts.splice(i, 1)
          continue
        }
        ctx.globalAlpha = p.life * 0.9
        ctx.fillStyle = 'rgba(' + p.color + ',1)'
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.r * (0.6 + p.life * 0.8), 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.globalAlpha = 1
      this.raf = requestAnimationFrame(this.tick)
    },
    emitBurst () {
      if (this.noMotion || !this.ctx) return
      const W = this.stageW
      const H = this.stageH
      const cx = W * (0.25 + Math.random() * 0.5)
      const cy = H * (0.3 + Math.random() * 0.4)
      for (let i = 0; i < 26; i++) {
        const ang = Math.random() * Math.PI * 2
        const sp = 0.6 + Math.random() * 1.9
        this.bursts.push({
          x: cx,
          y: cy,
          vx: Math.cos(ang) * sp,
          vy: Math.sin(ang) * sp - 0.4,
          r: 0.8 + Math.random() * 1.8,
          life: 0.7 + Math.random() * 0.5,
          color: COLORS[(Math.random() * COLORS.length) | 0]
        })
      }
    },
    resizeStars () {
      const canvas = this.$refs.stars
      const stage = this.$refs.stage
      if (!canvas || !stage) return
      const rect = stage.getBoundingClientRect()
      this.stageW = rect.width
      this.stageH = rect.height
      this.dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = Math.round(rect.width * this.dpr)
      canvas.height = Math.round(rect.height * this.dpr)
      canvas.style.width = rect.width + 'px'
      canvas.style.height = rect.height + 'px'
      const ctx = canvas.getContext('2d')
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
    },

    /* ---------- 交互：键盘 / 拖拽 / 可见性 ---------- */
    onKey (e) {
      const t = e.target
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        this.go(-1)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        this.go(1)
      }
    },
    onPointerDown (e) {
      if (this.slides.length < 2) return
      if (e.target && e.target.closest && e.target.closest('.hero__arrow, .hero__dot, .hero__brand')) return
      this.pointer = { id: e.pointerId, x: e.clientX, y: e.clientY, startX: e.clientX, startY: e.clientY, moved: false }
      this.pauseTimer()
      const stage = this.$refs.stage
      if (stage && stage.setPointerCapture) stage.setPointerCapture(e.pointerId)
    },
    onPointerMove (e) {
      if (!this.pointer || this.pointer.id !== e.pointerId) return
      const dx = e.clientX - this.pointer.startX
      const dy = e.clientY - this.pointer.startY
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) this.pointer.moved = true
      this.pointer.x = e.clientX
      this.pointer.y = e.clientY
    },
    onPointerUp (e) {
      if (!this.pointer || this.pointer.id !== e.pointerId) return
      const p = this.pointer
      this.pointer = null
      const dx = e.clientX - p.startX
      const dy = e.clientY - p.startY
      if (p.moved && Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy)) {
        this.go(dx < 0 ? 1 : -1)
      } else {
        this.resumeTimer()
      }
    },
    onVisibility () {
      this.pageHidden = document.hidden
      if (document.hidden) {
        this.pauseTimer()
        if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0 }
        if (this.kenRaf) { cancelAnimationFrame(this.kenRaf); this.kenRaf = 0 }
      } else {
        if (!this.noMotion) {
          this.resizeStars()
          if (!this.raf) this.raf = requestAnimationFrame(this.tick)
        }
        this.startKen()
        this.resumeTimer()
      }
    },
    onResize () {
      if (this._rsT) return
      this._rsT = setTimeout(() => {
        this._rsT = null
        if (!this.noMotion) this.resizeStars()
      }, 120)
    },
    attachListeners () {
      window.addEventListener('keydown', this.onKey)
      document.addEventListener('visibilitychange', this.onVisibility)
      window.addEventListener('resize', this.onResize)
    },
    detachListeners () {
      window.removeEventListener('keydown', this.onKey)
      document.removeEventListener('visibilitychange', this.onVisibility)
      window.removeEventListener('resize', this.onResize)
    }
  }
}
</script>

<style>
/* 毛笔书法标题字体（-fixed 版本可正确解析，避免 OTS 报错） */
@font-face {
  font-family: 'MoFa-ShuFa';
  src: url('~@/assets/fonts/书法毛笔-fixed.ttf') format('truetype');
  font-display: swap;
}
</style>

<style scoped>
.home {
  position: relative;
  overflow: hidden;
  min-height: calc(100vh - 60px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(12px, 2vw, 28px);
  box-sizing: border-box;
  background-color: #0a0514;
  background-image:
    radial-gradient(1100px 520px at 12% -8%, rgba(255, 158, 205, 0.14), transparent 60%),
    radial-gradient(900px 500px at 96% 6%, rgba(160, 130, 255, 0.16), transparent 55%),
    radial-gradient(1000px 380px at 72% 18%, rgba(190, 165, 255, 0.12), transparent 65%),
    linear-gradient(150deg, rgba(255, 255, 255, 0.05) 0%, transparent 28%, transparent 72%, rgba(143, 211, 255, 0.06) 100%),
    linear-gradient(160deg, #1c0f2e 0%, #130a22 52%, #0a0514 100%);
}

/* 星空背景：两层 SVG 星点 tile 平铺 + 闪烁（自适应任意屏幕大小） */
.home::before,
.home::after {
  content: '';
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-repeat: repeat;
}
.home::before {
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22320%22%20height%3D%22320%22%3E%3Ccircle%20cx%3D%2276.2%22%20cy%3D%22127.2%22%20r%3D%220.50%22%20fill%3D%22rgba(220%2C225%2C255%2C0.55)%22%2F%3E%3Ccircle%20cx%3D%22125.5%22%20cy%3D%22272.4%22%20r%3D%220.67%22%20fill%3D%22rgba(255%2C255%2C255%2C0.7)%22%2F%3E%3Ccircle%20cx%3D%22236.0%22%20cy%3D%2251.1%22%20r%3D%220.42%22%20fill%3D%22rgba(255%2C244%2C250%2C0.6)%22%2F%3E%3Ccircle%20cx%3D%22142.0%22%20cy%3D%22106.6%22%20r%3D%220.63%22%20fill%3D%22rgba(220%2C225%2C255%2C0.55)%22%2F%3E%3Ccircle%20cx%3D%22309.5%22%20cy%3D%22180.7%22%20r%3D%220.77%22%20fill%3D%22rgba(255%2C244%2C250%2C0.6)%22%2F%3E%3Ccircle%20cx%3D%22203.0%22%20cy%3D%2276.2%22%20r%3D%220.65%22%20fill%3D%22rgba(220%2C225%2C255%2C0.55)%22%2F%3E%3Ccircle%20cx%3D%22105.5%22%20cy%3D%22115.2%22%20r%3D%220.70%22%20fill%3D%22rgba(220%2C225%2C255%2C0.55)%22%2F%3E%3Ccircle%20cx%3D%22313.3%22%20cy%3D%22316.8%22%20r%3D%220.84%22%20fill%3D%22rgba(255%2C244%2C250%2C0.6)%22%2F%3E%3Ccircle%20cx%3D%22131.6%22%20cy%3D%2230.8%22%20r%3D%220.90%22%20fill%3D%22rgba(220%2C225%2C255%2C0.55)%22%2F%3E%3Ccircle%20cx%3D%22189.1%22%20cy%3D%22319.3%22%20r%3D%220.59%22%20fill%3D%22rgba(220%2C225%2C255%2C0.55)%22%2F%3E%3Ccircle%20cx%3D%2294.5%22%20cy%3D%2244.9%22%20r%3D%220.60%22%20fill%3D%22rgba(255%2C255%2C255%2C0.7)%22%2F%3E%3Ccircle%20cx%3D%2228.3%22%20cy%3D%22154.0%22%20r%3D%220.65%22%20fill%3D%22rgba(220%2C225%2C255%2C0.55)%22%2F%3E%3Ccircle%20cx%3D%22206.7%22%20cy%3D%2223.4%22%20r%3D%220.59%22%20fill%3D%22rgba(255%2C255%2C255%2C0.7)%22%2F%3E%3Ccircle%20cx%3D%2259.1%22%20cy%3D%2269.1%22%20r%3D%220.46%22%20fill%3D%22rgba(220%2C225%2C255%2C0.55)%22%2F%3E%3C%2Fsvg%3E");
  background-size: 320px 320px;
  animation: starTwinkle 6s ease-in-out infinite;
}
.home::after {
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22480%22%20height%3D%22480%22%3E%3Ccircle%20cx%3D%22242.7%22%20cy%3D%22448.8%22%20r%3D%220.93%22%20fill%3D%22rgba(255%2C214%2C235%2C0.85)%22%2F%3E%3Ccircle%20cx%3D%22395.9%22%20cy%3D%22291.4%22%20r%3D%220.98%22%20fill%3D%22rgba(201%2C181%2C255%2C0.85)%22%2F%3E%3Ccircle%20cx%3D%22175.5%22%20cy%3D%2212.6%22%20r%3D%221.06%22%20fill%3D%22rgba(255%2C214%2C235%2C0.85)%22%2F%3E%3Ccircle%20cx%3D%22402.9%22%20cy%3D%22148.9%22%20r%3D%220.72%22%20fill%3D%22rgba(201%2C181%2C255%2C0.85)%22%2F%3E%3Ccircle%20cx%3D%22305.9%22%20cy%3D%22262.7%22%20r%3D%221.34%22%20fill%3D%22rgba(255%2C214%2C235%2C0.85)%22%2F%3E%3Ccircle%20cx%3D%22107.7%22%20cy%3D%22425.5%22%20r%3D%220.82%22%20fill%3D%22rgba(201%2C181%2C255%2C0.85)%22%2F%3E%3Ccircle%20cx%3D%2296.6%22%20cy%3D%22401.9%22%20r%3D%221.16%22%20fill%3D%22rgba(255%2C233%2C196%2C0.8)%22%2F%3E%3Ccircle%20cx%3D%22456.9%22%20cy%3D%2277.7%22%20r%3D%220.88%22%20fill%3D%22rgba(255%2C233%2C196%2C0.8)%22%2F%3E%3Ccircle%20cx%3D%22343.9%22%20cy%3D%22368.8%22%20r%3D%220.95%22%20fill%3D%22rgba(255%2C233%2C196%2C0.8)%22%2F%3E%3C%2Fsvg%3E");
  background-size: 480px 480px;
  animation: starTwinkle 8s ease-in-out -3s infinite;
}

@keyframes starTwinkle {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

.home__empty {
  position: relative;
  z-index: 1;
  color: rgba(255, 225, 240, 0.75);
  letter-spacing: 0.2em;
  font-size: 14px;
}

.hero {
  position: relative;
  z-index: 1;
  width: min(1180px, 100%);
}

/* ============ 玻璃画框 ============ */
.hero__frame {
  position: relative;
  padding: 10px;
  border-radius: 34px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.05) 42%, rgba(255, 255, 255, 0.16));
  -webkit-backdrop-filter: blur(16px);
  backdrop-filter: blur(16px);
  box-shadow:
    0 34px 90px rgba(24, 8, 44, 0.55),
    0 10px 30px rgba(24, 8, 44, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

/* 画框角星装饰 */
.hero__frame::before,
.hero__frame::after {
  content: '✦';
  position: absolute;
  font-size: 15px;
  color: rgba(255, 220, 240, 0.75);
  text-shadow: 0 0 12px rgba(255, 170, 220, 0.85);
  animation: cornerFloat 5s ease-in-out infinite;
  pointer-events: none;
}
.hero__frame::before { top: 16px; right: 22px; }
.hero__frame::after { bottom: 16px; left: 22px; animation-delay: -2.5s; }

/* ============ 舞台 ============ */
.hero__stage {
  position: relative;
  height: clamp(360px, 56vh, 660px);
  border-radius: 26px;
  overflow: hidden;
  background: #150a20;
  cursor: grab;
  touch-action: pan-y;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.hero__stage:active { cursor: grabbing; }

/* ============ 幻灯片 ============ */
.slide {
  position: absolute;
  inset: 0;
  z-index: 1;
  opacity: 0;
  transform: scale(1.05);
  filter: blur(12px) brightness(0.85);
  transition:
    opacity 0.7s cubic-bezier(0.22, 0.61, 0.36, 1),
    transform 0.7s cubic-bezier(0.22, 0.61, 0.36, 1),
    filter 0.7s cubic-bezier(0.22, 0.61, 0.36, 1);
}
.slide.is-next {
  z-index: 2;
  opacity: 0.45;
  transform: scale(1.03);
  filter: blur(10px) brightness(0.72);
}
.slide.is-active {
  z-index: 3;
  opacity: 1;
  transform: scale(1);
  filter: blur(0) brightness(1);
  transition-duration: 1.6s, 1.6s, 1.6s;
}

.slide__media {
  position: absolute;
  inset: 0;
  overflow: hidden;
  will-change: transform;
  background: linear-gradient(135deg, #241a38, #0f0a1a);
}
.slide__media img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  user-select: none;
  -webkit-user-select: none;
  -webkit-user-drag: none;
}
.slide__shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(255, 170, 220, 0.1), transparent 38%),
    linear-gradient(180deg, rgba(20, 8, 34, 0.14) 0%, rgba(20, 8, 34, 0) 34%, rgba(16, 6, 30, 0.4) 72%, rgba(12, 4, 24, 0.74) 100%);
}

/* ============ 诗意文案 ============ */
.slide__caption {
  position: absolute;
  left: clamp(20px, 4vw, 52px);
  bottom: clamp(58px, 9vh, 96px);
  max-width: min(620px, 82%);
  z-index: 12;
  color: #fff;
  pointer-events: none;
  text-shadow: 0 2px 26px rgba(20, 8, 40, 0.5);
}
.slide__eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  opacity: 0;
  transform: translateY(14px);
  transition: opacity 0.5s ease, transform 0.5s ease;
}
.slide__eyebrow-num {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 13px;
  letter-spacing: 0.14em;
  color: #ffd9ec;
}
.slide__eyebrow-line {
  width: 34px;
  height: 1px;
  background: linear-gradient(90deg, rgba(255, 200, 230, 0.85), transparent);
}
.slide__eyebrow-en {
  font-size: 11px;
  letter-spacing: 0.3em;
  color: rgba(255, 236, 246, 0.78);
  text-transform: uppercase;
}
.slide__title {
  margin: 14px 0 8px;
  font-family: 'MoFa-ShuFa', 'STKaiti', 'KaiTi', 'Microsoft YaHei', serif;
  font-size: clamp(30px, 4vw, 52px);
  font-weight: 400;
  line-height: 1.22;
  letter-spacing: 0.14em;
  background: linear-gradient(118deg, #ffffff 18%, #ffd9ec 46%, #e6d6ff 72%, #ffffff 92%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  opacity: 0;
  transform: translateY(24px);
  filter: blur(6px);
  transition:
    opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1),
    transform 0.9s cubic-bezier(0.16, 1, 0.3, 1),
    filter 0.9s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide__sub {
  margin: 0;
  font-size: clamp(13px, 1.25vw, 16px);
  line-height: 1.85;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.9);
  opacity: 0;
  transform: translateY(16px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.slide.is-active .slide__eyebrow {
  opacity: 1;
  transform: none;
  transition-delay: 0.2s;
}
.slide.is-active .slide__title {
  opacity: 1;
  transform: none;
  filter: none;
  transition-delay: 0.38s;
}
.slide.is-active .slide__sub {
  opacity: 1;
  transform: none;
  transition-delay: 0.56s;
}

/* ============ 星尘 / 光晕 / 颗粒 / 暗角 ============ */
.hero__stars {
  position: absolute;
  inset: 0;
  z-index: 5;
  pointer-events: none;
  mix-blend-mode: screen;
}
.hero__sweep {
  position: absolute;
  inset: 0;
  z-index: 6;
  pointer-events: none;
  mix-blend-mode: screen;
  background: radial-gradient(ellipse 62% 52% at 50% 42%, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0) 70%);
  animation: heroSweep 0.95s cubic-bezier(0.2, 0.6, 0.3, 1) both;
}
.hero__vignette {
  position: absolute;
  inset: 0;
  z-index: 7;
  pointer-events: none;
  background: radial-gradient(ellipse 78% 70% at 50% 44%, transparent 54%, rgba(18, 6, 36, 0.42) 100%);
}
.hero__grain {
  position: absolute;
  inset: 0;
  z-index: 8;
  pointer-events: none;
  opacity: 0.05;
  mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix type='saturate' values='0'/></filter><rect width='160' height='160' filter='url(%23n)' opacity='0.55'/></svg>");
}

/* ============ 品牌角标 ============ */
.hero__brand {
  position: absolute;
  top: 18px;
  left: clamp(18px, 3vw, 36px);
  z-index: 12;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 15px;
  border-radius: 999px;
  background: rgba(20, 8, 36, 0.34);
  border: 1px solid rgba(255, 255, 255, 0.16);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  color: rgba(255, 255, 255, 0.88);
  font-size: 11px;
  letter-spacing: 0.22em;
  pointer-events: none;
}
.hero__brand-star {
  color: #ffd9ec;
  font-size: 13px;
  animation: cornerFloat 3s ease-in-out infinite;
}

/* ============ 方向按钮 ============ */
.hero__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 14;
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.26);
  background: rgba(22, 10, 38, 0.36);
  color: #fff;
  cursor: pointer;
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  transition: background 0.3s ease, border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
}
.hero__arrow--prev { left: 18px; }
.hero__arrow--next { right: 18px; }
.hero__arrow:hover {
  background: rgba(255, 170, 220, 0.28);
  border-color: rgba(255, 220, 240, 0.65);
  transform: translateY(-50%) scale(1.08);
  box-shadow: 0 0 26px rgba(255, 170, 220, 0.4);
}
.hero__arrow:focus-visible,
.hero__dot:focus-visible {
  outline: 2px solid #ffb3d9;
  outline-offset: 2px;
}

/* ============ 序号与指示点 ============ */
.hero__meta {
  position: absolute;
  left: clamp(18px, 3vw, 36px);
  right: clamp(18px, 3vw, 36px);
  bottom: 26px;
  z-index: 13;
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: none;
}
.hero__fraction {
  display: flex;
  align-items: baseline;
  gap: 7px;
  font-family: Georgia, 'Times New Roman', serif;
  color: #ffd9ec;
  letter-spacing: 0.12em;
}
.hero__fraction-cur { font-size: 22px; color: #fff; }
.hero__fraction-slash { font-size: 13px; opacity: 0.5; }
.hero__fraction-total { font-size: 13px; opacity: 0.75; }

.hero__dots {
  display: flex;
  gap: 10px;
  pointer-events: auto;
}
.hero__dot {
  position: relative;
  width: 26px;
  height: 26px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
}
.hero__dot-core {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.45);
  background: transparent;
  transition: background 0.35s ease, border-color 0.35s ease, transform 0.35s ease, box-shadow 0.35s ease;
}
.hero__dot:hover .hero__dot-core {
  border-color: #fff;
  transform: scale(1.25);
}
.hero__dot.is-active .hero__dot-core {
  width: 10px;
  height: 10px;
  border: none;
  background: linear-gradient(135deg, #ff9ecb, #b79cff);
  box-shadow: 0 0 14px rgba(255, 150, 200, 0.85);
}
.hero__dot.is-active::after {
  content: '';
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  border: 1px solid rgba(255, 190, 225, 0.4);
  animation: dotPulse 1.9s ease-out infinite;
}

/* ============ 进度条 ============ */
.hero__bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  z-index: 14;
  background: rgba(255, 255, 255, 0.09);
}
.hero__bar-fill {
  display: block;
  width: 100%;
  height: 100%;
  transform-origin: left center;
  background: linear-gradient(90deg, #ffb3d9, #c9a9ff 60%, #8fd3ff);
  box-shadow: 0 0 12px rgba(255, 170, 220, 0.65);
  animation: heroBar var(--bar-dur, 6500ms) linear forwards;
}
.hero__bar-fill.is-paused { animation-play-state: paused; }

/* ============ 提示 / 加载 ============ */
.hero__hint {
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: rgba(255, 238, 250, 0.55);
  font-size: 12px;
  letter-spacing: 0.16em;
}
.hero__hint-line {
  width: 28px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 200, 230, 0.5), transparent);
}

.hero__loading {
  position: absolute;
  inset: 10px;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  border-radius: 26px;
  background:
    radial-gradient(500px 300px at 50% 30%, rgba(255, 160, 210, 0.14), transparent 65%),
    linear-gradient(160deg, #251638, #130b22);
  color: #ffd9ec;
}
.hero__loading-star {
  font-size: 26px;
  animation: starSpin 1.6s ease-in-out infinite;
  text-shadow: 0 0 18px rgba(255, 170, 220, 0.9);
}
.hero__loading-text {
  font-size: 12px;
  letter-spacing: 0.34em;
  opacity: 0.72;
}

.hero-fade-enter-active,
.hero-fade-leave-active { transition: opacity 0.7s ease; }
.hero-fade-enter,
.hero-fade-leave-to { opacity: 0; }

/* ============ 动效关键帧 ============ */
@keyframes heroSweep {
  0% { opacity: 0; }
  18% { opacity: 0.8; }
  100% { opacity: 0; }
}
@keyframes heroBar {
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
}
@keyframes dotPulse {
  0% { transform: scale(0.6); opacity: 0.9; }
  100% { transform: scale(1.7); opacity: 0; }
}
@keyframes cornerFloat {
  0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.75; }
  50% { transform: translateY(-7px) rotate(20deg); opacity: 1; }
}
@keyframes starSpin {
  0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
  50% { transform: scale(1.25) rotate(20deg); opacity: 0.6; }
}

/* ============ 减少动态效果偏好 ============ */
.hero.is-no-motion .slide {
  transition-duration: 0.35s, 0.35s, 0.35s;
}
.hero.is-no-motion .hero__stars,
.hero.is-no-motion .hero__sweep {
  display: none;
}
.hero.is-no-motion .hero__bar-fill {
  animation: none;
  transform: scaleX(1);
}
.home::before,
.home::after {
  animation: none;
}

/* ============ 响应式 ============ */
@media (max-width: 720px) {
  .hero__arrow {
    width: 38px;
    height: 38px;
  }
  .hero__arrow--prev { left: 10px; }
  .hero__arrow--next { right: 10px; }
  .hero__brand { font-size: 10px; padding: 6px 12px; }
  .hero__hint { flex-wrap: wrap; gap: 8px; }
  .hero__hint-line { display: none; }
}
</style>
